import { type ReactNode } from 'react';
import { useUser } from '@/lib/auth';
import { useFetchCategoryByWithAuthUser } from '@/shared/category/useFetchCategoryInfo';
import { DetailPageLayout, MobileCTAFooter } from '@/shared/components';
import { DelayedLoadingFallback } from '@/shared/components/DelayedLoadingFallback';
import { ProductContentFrame } from '@/shared/components/ProductContentFrame';
import { useFolderDetail, useIsFolderOwner } from '@/shared/features/quiz-folder';
import { FolderErrorFallback } from '@/shared/features/quiz-folder/components/FolderErrorFallback';
import { getFolderHeroGradient } from '@/shared/features/quiz-folder/utils/folderHeroGradient';
import { useIsInProductContentFrame } from '@/shared/hooks/useIsInProductContentFrame';
import { PathParentLinkButton, useDeclareOwnership, useNavigationPathLabel } from '@/shared/modules/navigation-path';
import { cn } from '@/utils/tailwind.util';
import { FolderHeroCard } from './components/FolderHeroCard';
import { FolderPromoCard } from './components/FolderPromoCard';
import { FolderQuizSection } from './components/FolderQuizSection';
import { useFolderDetailPageView } from './hooks/useFolderDetailPageView';
import { FolderCtaPanel, FolderMobileCta } from './modules/folder-cta';

/**
 * 폴더 공개 상세. 로그인 없이 열 수 있는 공개 페이지라 `withPageAuth` 를 걸지 않는다.
 * 비공개 폴더도 URL 상세 조회는 가능하며, 삭제된 폴더는 SSR 에서 404 로 처리한다.
 *
 * Suspense 를 쓰지 않는다. SSR 이 상세를 `dehydrate` 로 넘겨 첫 렌더에 데이터가 있고,
 * 경계를 두면 하이드레이션 중 상태 변화에 서버 HTML 이 버려진다.
 * SSR 이 껍데기만 준 경우(인증 오류)에는 클라이언트 조회가 끝날 때까지 로딩을 그린다.
 */
export const FolderDetailPageView = ({
  id
}: {
  id: string;
}) => {
  const {
    data: folder,
    isError,
    refetch
  } = useFolderDetail({
    variables: {
      id
    },
    throwOnError: false
  });
  // 폴더 칩은 서버가 준 과목 이름을 쓴다. 이 조회는 담긴 퀴즈 행의 과목 이름용이다.
  useFetchCategoryByWithAuthUser();
  const {
    user,
    isInitialized
  } = useUser();
  // 남의 폴더에는 재생 CTA 를 두지 않는다 — 체험은 3문제에서 잠겨 폴더를 이어 풀 수 없다.
  // 대신 퀴즈 행마다 `내 퀴즈로 저장` 을 둔다.
  const isOwner = useIsFolderOwner(folder?.ownerId);
  // 길에 메뉴가 없으면 내 폴더 여부로 좌측 메뉴를 정한다.
  // 로그인·폴더 확인 전에는 null 로 두어 메뉴·목록 버튼이 아무것도 고르지 않는다.
  const isOwnershipKnown = !!folder && isInitialized;
  useDeclareOwnership(isOwnershipKnown ? isOwner : null);
  // 여기서 연 퀴즈의 목록 버튼이 폴더 이름을 보이도록 등록한다.
  useNavigationPathLabel(`/folder/${id}`, folder?.name);

  // 로그인 사용자는 여기서 상세를 한 번 다시 받는다(SSR 응답은 익명 기준) — 그 응답으로 `page_view` 도 찍는다.
  useFolderDetailPageView({
    folder,
    isOwner,
    viewerId: user?.quizUserId,
    refetch
  });

  // 남의 폴더 링크로 온 비로그인 사용자도 갈 수 있는 곳으로 보낸다(`/myquiz/*` 는 로그인이 필요하다).
  if (isError) return <FolderErrorFallback actionHref={'/public'} />;
  if (!folder) return <DelayedLoadingFallback delayMS={250} />;
  return <ProductContentFrame
  // 담긴 퀴즈 첫 쪽은 폴더 상세에 함께 와서, 여기까지 오면 목록이 그려진다.
  ready parentLink={isOwnershipKnown ? <PathParentLinkButton /> : undefined} footer={
  // 남의 폴더는 CTA 가 없어 하단 CTA 바를 띄우지 않는다.
  isOwner && <MobileCTAFooter>
            <FolderMobileCta folder={folder} />
          </MobileCTAFooter>} data-sentry-element="ProductContentFrame" data-sentry-component="FolderDetailPageView" data-sentry-source-file="FolderDetailPageView.tsx">
      <DetailPageLayout
    // 클래스 상세처럼 폴더 id 로 색을 고른다 — 폴더마다 항상 같은 색이다.
    bannerBackground={getFolderHeroGradient(folder)} hero={<FolderHeroCard folder={folder} />} aside={isOwner ? <StickyAside className={'z-10'}>
              <FolderCtaPanel folder={folder} />
            </StickyAside> :
    // 남의 폴더에는 프로모션 카드만 둔다.
    <StickyAside className={'pt-xl'}>
              <FolderPromoCard folderId={folder.id} />
            </StickyAside>} data-sentry-element="DetailPageLayout" data-sentry-source-file="FolderDetailPageView.tsx">
        <FolderQuizSection folder={folder} data-sentry-element="FolderQuizSection" data-sentry-source-file="FolderDetailPageView.tsx" />
      </DetailPageLayout>
    </ProductContentFrame>;
};

/** 사이드 블록. 바깥 `flex-1` 이 있어야 sticky 가 움직일 높이를 갖는다. */
const StickyAside = ({
  className,
  children
}: {
  className?: string;
  children: ReactNode;
}) => {
  // 카드 안에서는 카드 스크롤 영역 기준, 카드가 없으면(외부 플랫폼) 창 기준으로 붙는다.
  const inFrame = useIsInProductContentFrame();
  return <div className={'flex-1'} data-sentry-component="StickyAside" data-sentry-source-file="FolderDetailPageView.tsx">
      <div className={cn('sticky', inFrame ? 'top-[82px]' : 'top-[140px]', className)}>
        {children}
      </div>
    </div>;
};